import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  CalendarDaysIcon,
  TruckIcon,
  UserIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import { fuelTransactionsService } from '../../services/logistics/fuel-transactions';
import { shipmentsService } from '../../services/logistics/shipments';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';
import { formatNumber } from '../../utils/formatting';

const FuelManualAssignmentModal = ({ isOpen, onClose, onAssignmentComplete, unmatchedTransactionsData = null }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  
  const [unmatchedTransactions, setUnmatchedTransactions] = useState([]);
  const [matchedTransactions, setMatchedTransactions] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [shipmentSearchTerm, setShipmentSearchTerm] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [showMatchedTransactions, setShowMatchedTransactions] = useState(false);
  const [loadingMatched, setLoadingMatched] = useState(false);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && currentCompany?.id) {
      loadData();
    }
  }, [isOpen, currentCompany?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // If we have unmatched transactions data passed as props, use that
      if (unmatchedTransactionsData && unmatchedTransactionsData.length > 0) {
        console.log('📋 Using provided unmatched transactions:', unmatchedTransactionsData.length);
        setUnmatchedTransactions(unmatchedTransactionsData);
        
        // Still load shipments
        const shipmentsData = await shipmentsService.getShipments(currentCompany.id);
        setShipments(shipmentsData);
      } else {
        // Fallback to loading from database
        const [transactions, shipmentsData] = await Promise.all([
          fuelTransactionsService.getUnmatchedFuelTransactions(currentCompany.id),
          shipmentsService.getShipments(currentCompany.id)
        ]);
        
        setUnmatchedTransactions(transactions);
        setShipments(shipmentsData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadMatchedTransactions = async () => {
    try {
      setLoadingMatched(true);
      const transactions = await fuelTransactionsService.getMatchedFuelTransactions(currentCompany.id);
      setMatchedTransactions(transactions);
    } catch (error) {
      console.error('Error loading matched transactions:', error);
      toast.error('Failed to load matched transactions');
    } finally {
      setLoadingMatched(false);
    }
  };

  // Filter transactions based on search
  const filteredTransactions = unmatchedTransactions.filter(transaction => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      transaction.vehicle_plate?.toLowerCase().includes(searchLower) ||
      transaction.invoice_number?.toString().toLowerCase().includes(searchLower) ||
      transaction.driver?.toLowerCase().includes(searchLower) ||
      transaction.station_name?.toLowerCase().includes(searchLower)
    );
  });

  // Filter shipments based on search
  const filteredShipments = shipments.filter(shipment => {
    if (!shipmentSearchTerm) return true;
    
    const searchLower = shipmentSearchTerm.toLowerCase();
    return (
      shipment.trackingNumber?.toLowerCase().includes(searchLower) ||
      shipment.vehiclePlate?.toLowerCase().includes(searchLower) ||
      shipment.driverName?.toLowerCase().includes(searchLower) ||
      shipment.origin?.toLowerCase().includes(searchLower) ||
      shipment.destination?.toLowerCase().includes(searchLower)
    );
  });

  // Handle assignment
  const handleAssignment = async () => {
    if (!selectedTransaction || !selectedShipment) {
      toast.error('Please select both a transaction and a shipment');
      return;
    }

    try {
      setAssigning(true);
      await fuelTransactionsService.assignToShipment(
        selectedTransaction.id,
        selectedShipment.id
      );

      // Remove assigned transaction from the list
      setUnmatchedTransactions(prev => 
        prev.filter(t => t.id !== selectedTransaction.id)
      );

      // Clear selections
      setSelectedTransaction(null);
      setSelectedShipment(null);

      toast.success('Transaction assigned successfully');

      // Refresh matched transactions if they're being shown
      if (showMatchedTransactions) {
        loadMatchedTransactions();
      }

      // Notify parent if callback provided
      if (onAssignmentComplete) {
        onAssignmentComplete();
      }
    } catch (error) {
      console.error('Error assigning transaction:', error);
      toast.error('Failed to assign transaction');
    } finally {
      setAssigning(false);
    }
  };

  // Format date for display (always Georgian DD/MM/YYYY)
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format date and time for display
  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('fuel.manual_assignment')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Matched Transactions Section */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => {
                    setShowMatchedTransactions(!showMatchedTransactions);
                    if (!showMatchedTransactions && matchedTransactions.length === 0) {
                      loadMatchedTransactions();
                    }
                  }}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                >
                  <h3 className="text-lg font-medium text-gray-900">
                    المعاملات المطابقة ({matchedTransactions.length})
                  </h3>
                  <div className="flex items-center space-x-2">
                    {loadingMatched && <LoadingSpinner size="sm" />}
                    <svg
                      className={`w-5 h-5 transform transition-transform ${
                        showMatchedTransactions ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                
                {showMatchedTransactions && (
                  <div className="border-t border-gray-200 max-h-60 overflow-y-auto">
                    {matchedTransactions.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        لا توجد معاملات مطابقة
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {matchedTransactions.map((transaction) => (
                          <div key={transaction.id} className="p-4 bg-green-50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <div className="font-medium text-gray-900">
                                  معاملة #{transaction.invoice_number}
                                </div>
                                <div className="text-gray-600">
                                  📅 {formatDateTime(transaction.fill_time)}
                                </div>
                                <div className="text-gray-600">
                                  🚗 {transaction.mappedVehiclePlate || transaction.vehicle_plate}
                                </div>
                                <div className="text-gray-600">
                                  ⛽ {formatNumber(transaction.liters)} لتر - {formatNumber(transaction.cost)} ريال
                                </div>
                              </div>
                              <div>
                                {transaction.matchedShipment && (
                                  <>
                                    <div className="font-medium text-green-700">
                                      شحنة #{transaction.matchedShipment.trackingNumber}
                                    </div>
                                    <div className="text-gray-600">
                                      📅 {formatDateTime(transaction.matchedShipment.createdAt)}
                                    </div>
                                    <div className="text-gray-600">
                                      📍 {transaction.matchedShipment.origin} → {transaction.matchedShipment.destination}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Assignment Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ height: 'calc(90vh - 350px)' }}>
              {/* Unmatched Transactions */}
              <div className="flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('fuel.unmatched_transactions')} ({filteredTransactions.length})
                  </h3>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('fuel.search_transactions')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg min-h-0">
                  {filteredTransactions.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      {searchTerm ? t('fuel.no_matching_transactions') : t('fuel.no_unmatched_transactions')}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {filteredTransactions.map((transaction) => (
                        <div
                          key={transaction.id}
                          onClick={() => setSelectedTransaction(transaction)}
                          className={`p-4 cursor-pointer hover:bg-gray-50 relative ${
                            selectedTransaction?.id === transaction.id
                              ? 'bg-blue-50 border-l-4 border-blue-500'
                              : ''
                          }`}
                        >
                          <div className="space-y-3">
                            {/* Header with vehicle and invoice */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <TruckIcon className="h-4 w-4 text-gray-400" />
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {transaction.mappedVehiclePlate || transaction.vehicle_plate}
                                  </span>
                                  {transaction.mappedVehiclePlate && transaction.mappedVehiclePlate !== transaction.vehicle_plate && (
                                    <div className="text-xs text-blue-600">
                                      📋 {transaction.vehicle_plate} → {transaction.mappedVehiclePlate}
                                    </div>
                                  )}
                                  {transaction.enrichedVehicleInfo && transaction.enrichedVehicleInfo !== 'Unknown Vehicle' && (
                                    <div className="text-xs text-green-600">
                                      ✅ {transaction.enrichedVehicleInfo}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-medium text-gray-900">
                                  #{transaction.invoice_number}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatDateTime(transaction.fill_time)}
                                </div>
                              </div>
                            </div>
                            
                            {/* Transaction details */}
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">الكمية:</span>
                                <span className="font-medium ml-1">{formatNumber(transaction.liters)} لتر</span>
                              </div>
                              <div>
                                <span className="text-gray-500">التكلفة:</span>
                                <span className="font-medium ml-1">{formatNumber(transaction.cost)} ريال</span>
                              </div>
                              <div>
                                <span className="text-gray-500">المحطة:</span>
                                <span className="font-medium ml-1">{transaction.station_name || 'غير محدد'}</span>
                              </div>
                            </div>
                            
                            {(!transaction.hasVehicleMatch) && (
                              <div className="text-xs text-red-600 mt-1">
                                ⚠ Unknown Vehicle
                              </div>
                            )}

                            {/* Additional info */}
                            <div className="space-y-1">
                              {transaction.driver && (
                                <div className="flex items-center space-x-1 text-xs text-gray-500">
                                  <UserIcon className="h-3 w-3" />
                                  <span>
                                    {transaction.enrichedDriverInfo && transaction.enrichedDriverInfo !== 'Unknown Driver' 
                                      ? transaction.enrichedDriverInfo 
                                      : `${transaction.driver} (Unknown Driver)`
                                    }
                                  </span>
                                </div>
                              )}

                              {transaction.station_name && (
                                <div className="flex items-center space-x-1 text-xs text-gray-500">
                                  <BuildingStorefrontIcon className="h-3 w-3" />
                                  <span className="truncate">{transaction.station_name}</span>
                                </div>
                              )}
                            </div>

                            {/* Action buttons */}
                            <div className="flex space-x-2 mt-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTransaction(transaction);
                                }}
                                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                              >
                                تعيين لشحنة
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // TODO: Implement transaction matching
                                  toast.info('ميزة مطابقة المعاملات قيد التطوير');
                                }}
                                className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                              >
                                مطابقة معاملة
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Available Shipments */}
              <div className="flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('fuel.available_shipments')} ({filteredShipments.length})
                  </h3>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('fuel.search_shipments')}
                      value={shipmentSearchTerm}
                      onChange={(e) => setShipmentSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg min-h-0">
                  {filteredShipments.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      {shipmentSearchTerm ? t('fuel.no_matching_shipments') : t('fuel.no_available_shipments')}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {filteredShipments.map((shipment) => (
                        <div
                          key={shipment.id}
                          onClick={() => setSelectedShipment(shipment)}
                          className={`p-4 cursor-pointer hover:bg-gray-50 ${
                            selectedShipment?.id === shipment.id
                              ? 'bg-green-50 border-l-4 border-green-500'
                              : ''
                          }`}
                        >
                          <div className="space-y-3">
                            {/* Header with shipment number and status */}
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-gray-900">
                                📦 {shipment.trackingNumber}
                              </div>
                              <div className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {shipment.status}
                              </div>
                            </div>
                            
                            {/* Route information */}
                            <div className="text-sm text-gray-600">
                              📍 {shipment.origin} → {shipment.destination}
                            </div>
                            
                            {/* Vehicle and driver info */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">المركبة:</span>
                                <span className="font-medium ml-1">{shipment.vehiclePlate || 'غير محدد'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">السائق:</span>
                                <span className="font-medium ml-1">{shipment.driverName || 'غير محدد'}</span>
                              </div>
                            </div>
                            
                            {/* Timing information */}
                            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                              {shipment.deliveryDate && (
                                <div>📅 تاريخ التسليم: {formatDateTime(shipment.deliveryDate)}</div>
                              )}
                              {shipment.pickupDate && (
                                <div>📅 تاريخ الاستلام: {formatDateTime(shipment.pickupDate)}</div>
                              )}
                              {!shipment.deliveryDate && !shipment.pickupDate && shipment.createdAt && (
                                <div>📅 تاريخ الإنشاء: {formatDateTime(shipment.createdAt)}</div>
                              )}
                            </div>

                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              {shipment.vehiclePlate && (
                                <div className="flex items-center space-x-1">
                                  <TruckIcon className="h-4 w-4" />
                                  <span>{shipment.vehiclePlate}</span>
                                </div>
                              )}
                              
                              {shipment.driverName && (
                                <div className="flex items-center space-x-1">
                                  <UserIcon className="h-4 w-4" />
                                  <span>{shipment.driverName}</span>
                                </div>
                              )}
                            </div>

                            <div className="text-xs text-gray-500">
                              {formatDateTime(shipment.deliveryDate || shipment.createdAt)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t">
          <div className="text-sm text-gray-600">
            {selectedTransaction && selectedShipment ? (
              <span>
                Ready to assign {selectedTransaction.vehicle_plate} transaction to {selectedShipment.trackingNumber}
              </span>
            ) : (
              <span>Select a transaction and a shipment to assign</span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            
            <button
              onClick={handleAssignment}
              disabled={!selectedTransaction || !selectedShipment || assigning}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {assigning && <LoadingSpinner size="sm" className="mr-2" />}
              <CheckIcon className="h-4 w-4 mr-2" />
              {t('fuel.assign_transaction')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuelManualAssignmentModal;